import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { isEmpty, isNil, startCase } from 'lodash'

import { Table } from '@/components/tables/Table/Table'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { fetchCallSummaryByCallId } from '@/reducers/callSearch/callSearch.actions'
import { ExternalLink } from '@/components/ExternalLink'
import { CopyButton } from '@/components/buttons/CopyButton'
import { dateFormat } from '@/utils/constants'
import { formatDuration } from '@/utils/helpers'

export const TechnicalDetails = ({ metadata = {}, callId }) => {
  const dispatch = useDispatch()
  const { qa_enabled, qa_copilot_enabled } = useSelector((state) => state.currentOrganization)
  const {
    organization_name: organizationName,
    voip_call_id: voipCallID,
    agent_name: agentName,
    call_start_time: callStartTime,
    call_end_time: callEndTime,
    config_name: configName,
    call_duration: callDuration = 0,
    is_win: isWin,
    username,
    call_uuid: callUUID,
    voip_customer_id: voipCustomerId,
    voip_campaign_name: voipCampaignName,
    disposition,
    qa_eligibility,
    config_id: configId,
    prompt_runs: copilotEligibility,
    voip_metadata: voipMetadata,
    postcall_only: postcallOnly,
    outbound_campaign_name: outboundCampaignName,
    ai_agent_outcome: aiAgentOutcome,
    ai_agent_disconnection_reason: aiAgentDisconnectionReason,
  } = metadata
  const showOutboundCampaign = !isNil(outboundCampaignName) || !isNil(aiAgentOutcome)

  const formattedMetadata = [
    { label: 'Organization', value: organizationName },
    { label: 'VOIP ID', value: voipCallID, isCopiable: true },
    { label: 'Agent', value: agentName },
    { label: 'Call Duration', value: formatDuration(callDuration) },
    { label: 'Start Time', value: callStartTime ? format(callStartTime, dateFormat.long) : null },
    { label: 'End Time', value: callEndTime ? format(callEndTime, dateFormat.long) : null },
    {
      label: 'Playbook',
      value: configName,
      component: (
        <ExternalLink
          id="playbook-link"
          to={`/playbooks/${configId}/checklist`}
          label={configName}
        />
      ),
    },
    { label: 'Username', value: username, isCopiable: true },
    // hide win marker for postcall only calls
    ...(postcallOnly
      ? []
      : [
          {
            label: 'Win',
            value: !!isWin,
            component: (
              <Pill small success={isWin} critical={!isWin}>
                {isWin ? 'Yes' : 'No'}
              </Pill>
            ),
          },
        ]),
    { label: 'Call UUID', value: callUUID, isCopiable: true },
    { label: 'VOIP Customer ID', value: voipCustomerId, isCopiable: true },
    { label: 'VOIP Campaign Name', value: voipCampaignName, isCopiable: true },
    { label: 'Disposition', value: disposition },
    ...(showOutboundCampaign
      ? [
          { label: 'Outbound Campaign', value: outboundCampaignName, isCopiable: true },
          {
            label: 'AI Agent Outcome',
            value: aiAgentOutcome ? startCase(aiAgentOutcome) : null,
          },
        ]
      : []),
    ...(aiAgentDisconnectionReason
      ? [{ label: 'AI Agent Disconnection Reason', value: startCase(aiAgentDisconnectionReason) }]
      : []),
  ]

  if (qa_enabled || qa_copilot_enabled) {
    const displayValue = !isEmpty(qa_eligibility)
      ? qa_eligibility.map((item) => {
          const promptRun = copilotEligibility?.filter((a) => {
            return a.config_name === item.config_name
          })
          if (!isEmpty(promptRun)) {
            const reason = promptRun[0].explanation
            return (
              <div key={item.config_name}>
                <InteractiveTooltip interactive content={reason}>
                  <div>
                    <strong>{item.config_name}</strong>:{' '}
                    <span>{reason.slice(0, reason.indexOf('.') + 1)}...</span>
                  </div>
                </InteractiveTooltip>
              </div>
            )
          }

          return (
            <div key={item.config_name}>
              <strong>{item.config_name}</strong>: {item.reason.replaceAll(/_/g, ' ')}
            </div>
          )
        })
      : 'N/A'

    formattedMetadata.push({
      label: 'QA Eligibility',
      value: displayValue,
    })
  }

  // while we allow storing any JSON in voip metadata, the reporting API will only return it
  // if it is a flat dict of key-value pairs. Otherwise the API returns null for this field.
  if (voipMetadata) {
    const displayVoipMetadata = Object.entries(voipMetadata).map(([key, val]) => (
      <div key={key}>
        <strong>{key}</strong>: {String(val)}
      </div>
    ))
    formattedMetadata.push({
      label: 'Call Metadata',
      value: displayVoipMetadata,
    })
  }

  useEffect(() => {
    dispatch(fetchCallSummaryByCallId(callId))
  }, [])

  return (
    <div className="call-metadata-container">
      <h2>Technical Details</h2>

      <Table compact celled>
        <tbody>
          {formattedMetadata.map((item) => {
            return (
              <tr key={item.label}>
                <td>
                  <strong>{item.label}</strong>
                </td>
                <td>
                  <div className="call-metadata-value-container">
                    <span className="muted-text pre-wrap" data-testid={item.label}>
                      {item.component || item.value || '--'}
                    </span>
                    {item.isCopiable && item.value && (
                      <CopyButton content={item.value} showTooltip iconOnly />
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )
}
